import type { ShadowGenerator } from '@babylonjs/core/Lights/Shadows/shadowGenerator';
import type { Mesh } from '@babylonjs/core/Meshes/mesh';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { CreateSphere } from '@babylonjs/core/Meshes/Builders/sphereBuilder';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import type { Scene } from '@babylonjs/core/scene';

import { highlightParts } from './outline';
import type { SnapshotBuffer } from '../net/interpolate';
import { activeEffects, EFFECT_HUE, type EffectKind } from '../ui/statusEffects';
import type { PlayerPredictor } from '../net/predict';
import { isElite, type EntitySnapshot, type Pools } from '../net/protocol';
import type { GameStore } from '../state/store';
import { lerpAngle, smoothingAlpha } from './lerp';
import { animStateFor, DEATH_SECONDS, type AnimState } from './models/animState';
import { DEFAULT_SHOT_SHAPE, shotShapeFor } from './bolt';
import { corpseAlpha, corpseFinished, fallAngle, MAX_CORPSES } from './corpse';
import { MIN_OUTLINE_WIDTH } from './pixelate';
import type { BoltField } from './bolts';
import { damageTypeColour } from './damage';
import {
  DODGE_SECONDS,
  castAnimSeconds,
  castProgressFor,
  castStyleForClip,
  type CastStyle,
} from './models/poses';
import { ProceduralModelFactory } from './models/procedural';
import type { EntityModel, EntityModelFactory, PoseContext, WornGear } from './models/types';

/**
 * How fast a model turns toward its heading, in e-foldings per second.
 *
 * Rotation is still smoothed rather than interpolated: facing changes are
 * instantaneous on the server (an entity snaps to face its target the moment it
 * acquires one) and interpolating that exactly would look like a twitch.
 */
const FACING_RATE = 14;

/**
 * How fast a channelling body spins, in radians per second. Phase 30.4.
 *
 * **One revolution per hit.** Whirlwind pulses every 0.4 s — a number its own description gives —
 * and 5π rad/s is two and a half turns a second, so each revolution lands one beat of damage. That
 * makes the spin readable as the thing it is doing rather than as decoration at an arbitrary rate,
 * and a player can count hits by watching the body.
 *
 * It is a constant rather than derived from the skill's `channel_tick_sec`, which the client does
 * hold: a second channelling skill with a different cadence would make this approximate, and at
 * that point it should be derived. There is one such skill today.
 */
const SPIN_RADIANS_PER_SECOND = Math.PI * 5;

/**
 * How much larger a pack champion is drawn.
 *
 * 1.35, not more: an elite has to read as "that one is dangerous" at a glance
 * from the gameplay camera, while still fitting through the corridor its pack
 * was placed in.
 */
export const ELITE_SCALE = 1.35;

/**
 * The hover outline.
 *
 * Warm rather than saturated, so it reads as "you can click this" rather than as
 * a status effect. The width is in world units and had to be raised from 0.035 —
 * at the 26-unit camera that came out around one device pixel, which was
 * technically an outline and practically invisible.
 */
/**
 * How far the hover outline stands off the body, in world units.
 *
 * **Exactly one output pixel, down from 0.2** (owner's report: *the overlay when hovering an
 * item and a mob is too invasive — if it could just outline the model with a small line that
 * would be best, instead of turning the mob completely yellow*).
 *
 * The history is a floor overshot into a ceiling. It was 0.08, which is *below* one output pixel
 * and so was averaged away entirely — invisible, set on every part exactly as intended. The fix
 * raised it to 0.2 with "generous rather than exact" as the reasoning, and generous is what went
 * wrong: **Babylon draws an outline by scaling the mesh, so the width is measured against the part
 * rather than against the screen.** A limb is 0.13–0.17 units thick, so 0.2 of rim on each side
 * was wider than the limb it surrounded, and every thin part — arms, legs, ribs, a blade — was
 * swallowed whole. The chest was fine, which is why it read as *some* of the monster turning
 * yellow rather than as an outline being wrong.
 *
 * `MIN_OUTLINE_WIDTH` is the honest number here: one output pixel is the thinnest line the
 * pixelate pass can carry, and a one-pixel rim is what "outline" means. It is a floor and it is
 * now also the value — there is no headroom above it that does not start filling limbs in.
 */
export const HIGHLIGHT_WIDTH = MIN_OUTLINE_WIDTH;
const HIGHLIGHT_COLOUR = new Color3(0.95, 0.8, 0.45);

interface Tracked {
  model: EntityModel;
  /**
   * The orb a buff hangs over this body, built on first need and kept for the entity's life.
   *
   * **A mesh rather than a shade**, at the owner's direction: *"they can be added models (like a
   * ball for mana shield on top of head), as long as it's visible"*. It is the better channel
   * anyway — a body wears one overlay colour, so a shade would make a stunned-and-shielded
   * character drop one of the two, and three of the four debuff hues already sit in the blue band.
   */
  buffOrb?: Mesh;
  kind: EntitySnapshot['kind'];
  /**
   * The armour class the model was built for, so a change can be spotted.
   *
   * A player's body is decided at build time and rebuilt when this moves — see
   * `models/armour.ts` for why rebuilding beats toggling pre-built layers.
   */
  armour: string | undefined;
  /**
   * The weapon type the model was built for, so a change can be spotted.
   *
   * Same rule as `armour`, and the same reason: the ten silhouettes differ in reach by
   * more than two units end to end, so pre-building them all and toggling `isVisible`
   * would leave every one of them in the rig — and hidden meshes still have bounds.
   * See `models/weapons.ts`.
   */
  weapon: string | undefined;
  /**
   * The rest of the worn look the model was built for — head, hands, legs, feet, off hand
   * — as one key, so the rebuild check below is one comparison however many slots the
   * wire grows. Phase 41. See `gearKey`.
   */
  gearKey: string;
  state: AnimState;
  /** Seconds into the death animation. Counts up once and stops. */
  deathProgress: number;
  /**
   * Seconds left of a cast animation, how long it runs in total, which movement it
   * is, and the skill's own cast time.
   *
   * The timer is started by `EVENT_SKILL_CAST_STARTED` rather than by the snapshot's
   * state, so the wind-up begins on the tick the server opened the window instead of
   * a tick later — the same reason the roll's timer runs off `EVENT_DODGED`. It is
   * the *event* that also makes a spectator see another player's cast, since both
   * halves ride the snapshot everyone receives.
   *
   * `castTimeSec` is kept because the pose's progress is not linear in time once a
   * cast has a length: `castProgressFor` stretches the wind-up to the cast and
   * leaves the follow-through fixed, so the commit frame lands on the tick the
   * server resolved. `castAwaiting` is true while a windowed cast is still expecting
   * that resolution, which is how an **interrupted** cast is told from a completed
   * one — the resolution event clears it, and the snapshot dropping `CASTING` with it
   * still set means the cast was abandoned.
   */
  castRemaining: number;
  castTotal: number;
  castTimeSec: number;
  castAwaiting: boolean;
  castStyle: CastStyle;
  /**
   * Seconds left of a roll animation, and the direction it is going.
   *
   * Unlike a cast the server *does* describe the state — the snapshot carries
   * `DODGING` — but not the progress and not the direction, so both are tracked here.
   * The timer runs off `EVENT_DODGED` rather than the state so the local player's roll
   * animates on the frame the key went down, alongside the movement the predictor
   * already moved on. Waiting for the state would start the body a tick after it had
   * begun sliding.
   */
  dodgeRemaining: number;
  dodgeFacing: number | null;
  /**
   * How far the body has been spun through a channel, in radians. Phase 30.4.
   *
   * **Per entity and accumulated, not derived from a clock.** A shared clock would have every
   * spinning character in a party locked to the same angle, and deriving it from the channel's
   * remaining time would need the client to know a duration the snapshot does not carry. It is
   * simply advanced while the server says CHANNELLING and left where it stopped otherwise —
   * a body that spins up again picks up from the angle it finished on, which is invisible and
   * free, where resetting to zero would snap the shoulders round on the first frame.
   */
  spinPhase: number;
  /** Last frame's position, to derive how far the model actually travelled. */
  lastX: number;
  lastZ: number;
}

/**
 * Keeps one model per server entity and places it every frame.
 *
 * Positions come from two different places, on purpose:
 *
 *  - **Other entities** are read from the snapshot buffer, interpolated between
 *    the two server states bracketing `now - INTERP_DELAY_MS`. Exact motion
 *    between two known truths.
 *  - **The local player** comes from the predictor, which ran the server's own
 *    movement rule at full frame rate the instant the click happened, and folds
 *    the authoritative position back in as it arrives.
 *
 * The alternative — which this replaced — was exponential smoothing toward the
 * newest snapshot for everything. That is a low-pass filter: it permanently trails
 * the truth, never arrives, and makes a 30 Hz server feel like a 30 Hz game.
 *
 * Nothing here reads a Solid signal for per-frame data, so a 144 fps render loop
 * never schedules a DOM update.
 */
export class EntityMeshes {
  private readonly tracked = new Map<string, Tracked>();

  /**
   * Bodies that have died and are still falling.
   *
   * **Detached from `tracked` on purpose.** A corpse has no snapshot, no interpolation, no
   * nameplate and no pick target — leaving it in the live map would mean every pass over
   * that map having to ask whether an entry is real, and would make a corpse targetable,
   * which is a worse bug than the vanishing this replaced. See `corpse.ts`.
   */
  private readonly corpses: { model: EntityModel; elapsed: number }[] = [];

  /**
   * Ids the last snapshot said died, so the removal that follows can tell a kill from a
   * disconnect. Cleared every frame — an id that is never removed simply lapses.
   */
  private readonly died = new Set<string>();
  private readonly factory: EntityModelFactory;

  /** Scratch, so updating allocates nothing. */
  private readonly ctx: PoseContext = {
    state: 'idle',
    cooldown: 0,
    distance: 0,
    deathProgress: 0,
    castProgress: 0,
    castStyle: 'weapon',
    spinPhase: 0,
    dodgeProgress: 0,
  };

  constructor(
    private readonly scene: Scene,
    private readonly shadows: ShadowGenerator,
    private readonly store: GameStore,
    private readonly buffer: SnapshotBuffer,
    private readonly predictor: PlayerPredictor,
    factory?: EntityModelFactory,
    /**
     * Where projectiles go. Optional so a test can build a registry without one,
     * and absent under `?safe=1` — a bolt is decoration over an outcome the server
     * already resolved, so leaving it out changes nothing that matters.
     */
    private readonly bolts?: BoltField,
  ) {
    this.factory = factory ?? new ProceduralModelFactory(scene);
  }

  /**
   * Advances every model one frame.
   *
   * `now` is a `performance.now()` reading, passed in rather than sampled here so
   * the whole frame shares one instant and the interpolation cannot tear.
   */
  update(dt: number, now: number): void {
    // **Deaths before removals**, because they are the same event arriving twice: the
    // server drops a dead mob from the snapshot on the tick it emits `EVENT_ENTITY_DIED`,
    // and "absent from the snapshot" is the one rule for disposing a mesh. Read the other
    // way round, every kill would dispose before anything knew it was a kill.
    this.died.clear();
    for (const id of this.store.takeDeaths()) this.died.add(id);

    for (const id of this.store.takeRemoved()) this.remove(id);

    this.advanceCorpses(dt);

    // Drained here, next to removals and for the same reason: both are things the
    // last snapshot said happened, and both are the render loop's to act on. Done
    // before the per-entity pass so a cast that arrived this frame animates on it.
    //
    // The **beginning** of a cast, which is what starts the body moving. Separate from
    // the resolution below because a cast time pulls the two apart by up to 25 ticks,
    // and because an interrupted cast has a beginning and no end at all.
    for (const start of this.store.takeCastStarts()) {
      // The movement comes from the skill's own catalogue entry — its `animationClip`
      // if the registry knows the id, its scaling type otherwise. The client has both
      // for every skill in the game, because the WELCOME catalogue is static and the
      // same for everyone, so another player's cast animates correctly too.
      // **A displacement is predicted here, on the frame the event arrives.** Only for the local
      // player: a remote entity's position is interpolated from snapshots and predicting it would
      // fight that. Without this the server carries the body 6 m while the client stands still, and
      // the reconciler — whose snap threshold is 3.5 — yanks it across in one frame.
      if (start.dash && start.entityId === this.store.selfId()) {
        this.predictor.dash(start.dash.to, start.dash.seconds);
      }

      const info = this.store.skillInfo(start.skillId);
      this.startCast(
        start.entityId,
        castStyleForClip(info?.animationClip, info?.scalingType ?? ''),
        info?.castTimeSec ?? 0,
      );
    }

    // The **resolution**: the damage is applied as of the snapshot carrying this, so
    // this is where a projectile may leave and nowhere else.
    for (const cast of this.store.takeCasts()) {
      const info = this.store.skillInfo(cast.skillId);

      // Land the animation's commit frame on the resolution. Only for a windowed cast:
      // an instant one resolves in the tick the key went down, so its wind-up is
      // retroactive licence and skipping it would turn every one of the 85 instant
      // skills into a twitch.
      this.commitCast(cast.entityId, info?.castTimeSec ?? 0);

      // And throw something, if the skill is one that throws. The caster's own
      // position comes from the mesh rather than the snapshot so the bolt leaves
      // where the body actually is on this frame.
      const caster = this.tracked.get(cast.entityId);
      if (this.bolts && caster) {
        const origin = {
          x: caster.model.root.position.x,
          z: caster.model.root.position.z,
        };
        this.bolts.cast(
          origin,
          // A cast with no direction — no target and no aim — is passed its own origin
          // rather than skipped. A thrown shape has nowhere to fly and `BoltField`
          // drops it, but a shape that blooms on the caster never needed a direction,
          // and a nova drawing nothing at all is the case this used to miss.
          cast.at ?? origin,
          info?.scalingType,
          info?.colour,
          // The shape decides the geometry, the wake and the impact. Absent for a skill
          // the catalogue says nothing about, which falls back to the orb every spell
          // had before shapes existed.
          info?.projectileShape,
        );
      }
    }

    // Blasts, drained beside the casts because they are the same kind of thing: the
    // server resolved the damage on the tick it sent the event, so the ring is decoration
    // over a settled outcome. It is drawn wherever the server said, not at a caster's
    // mesh — a slam lands on the *player*, and an explosion can be dropped on empty floor.
    for (const blast of this.store.takeBlasts()) {
      if (!this.bolts) continue;
      // The skill's own colour where there is a skill: a fire blast is orange and a
      // frost one blue, from the same catalogue entry the bolt reads. A monster's slam
      // has none, and falls back to the neutral the field uses for an unknown spell.
      const info = blast.skillId ? this.store.skillInfo(blast.skillId) : undefined;
      // The geometry travels with the event, so the footprint is the shape the damage
      // used — a ring for a stomp, a wedge for a cone, a corridor for a fissure. Drawing
      // a ring for all three would teach the player to aim the wrong way.
      this.bolts.blast(
        blast.at,
        blast.radius,
        info?.colour ?? null,
        blast.shape ?? 'TARGET_RADIAL',
        blast.angle ?? 0,
        blast.facing ?? 0,
      );
    }

    // A monster's ranged attack. Drained beside the casts because it is the same
    // kind of thing: the hit resolved server-side on the tick that sent the event, so
    // the bolt is decoration over a settled outcome and lands the same fixed
    // `BOLT_SECONDS` after its damage number as a spell's does.
    //
    // Both ends come from the meshes rather than the snapshot, so the shot leaves and
    // arrives where the bodies actually are on this frame — a remote entity renders
    // two ticks behind, and a bolt drawn to its snapshot position would miss it.
    for (const shot of this.store.takeShots()) {
      const from = this.tracked.get(shot.sourceId);
      const to = this.tracked.get(shot.targetId);
      if (!this.bolts || !from || !to) continue;
      this.bolts.shoot(
        { x: from.model.root.position.x, z: from.model.root.position.z },
        { x: to.model.root.position.x, z: to.model.root.position.z },
        damageTypeColour(shot.damageType),
        // **A player's shot is drawn as their weapon.** The shooter's `weapon_type` is
        // already on the snapshot, so an arrow is a shard and a wand bolt an orb without
        // a new wire field — the server says a shot happened and the client decides what
        // it looks like, the same division a skill's `projectileShape` follows. A monster
        // has no weapon and keeps the shard every monster fires.
        shotShapeFor(from.weapon, DEFAULT_SHOT_SHAPE),
      );
    }

    // Drained beside the casts and the removals, and before the per-entity pass so a
    // roll that arrived this frame animates on it.
    for (const roll of this.store.takeDodges()) {
      this.startDodge(roll.entityId, roll.direction);
    }

    const faceAlpha = smoothingAlpha(dt, FACING_RATE);
    const selfId = this.store.selfId();
    // The player's own buffs, for the world rim. Read once rather than per entity: it is the
    // same answer for every body in the loop, and only one body can wear it.
    const selfPools = this.store.character()?.pools;

    for (const [id, snap] of this.store.raw) {
      let entry = this.tracked.get(id) ?? this.create(id, snap);

      // A change of chest piece changes the body, and a change of main hand changes
      // the weapon and whether there is a shield at all. Rebuilt rather than swapped:
      // both happen on an equip and nowhere else, and rebuilding is what re-measures
      // the collider from the new body. Guarded by the comparison, so this is never
      // per-frame work.
      if (
        snap.armour !== entry.armour ||
        snap.weapon !== entry.weapon ||
        gearKey(snap) !== entry.gearKey
      ) {
        // **Discarded, not removed.** A rebuild is not a departure, and `remove` would
        // leave a corpse for anything the last snapshot said had died — a player emits
        // `EVENT_ENTITY_DIED` as well as `EVENT_PLAYER_DIED`, and a corpse rebuilt on the
        // frame they died would stand beside the body it came from.
        this.discard(id);
        entry = this.create(id, snap);
      }

      const root = entry.model.root;

      // Mid-roll the body faces along the roll, not along the server's facing.
      //
      // The two normally agree — the server sets `Facing` to the roll direction — but
      // the *event* carries the vector a tick before any snapshot does, and the dodge
      // pose pitches along +Z, so a body still turning when the roll starts would dive
      // sideways. Snapped rather than eased for the same reason the blend is skipped: a
      // roll is 0.3 s and there is no time to turn.
      const rollFacing = entry.dodgeRemaining > 0 ? entry.dodgeFacing : null;

      if (id === selfId) {
        // The local player is wherever prediction says. Its authoritative
        // position is fed to the predictor in main.tsx, not applied here.
        const predicted = this.predictor.current;
        root.position.x = predicted.x;
        root.position.z = predicted.z;

        root.rotation.y = lerpAngle(root.rotation.y, snap.facing, faceAlpha);
      } else {
        const sampled = this.buffer.sample(id, now);
        if (sampled) {
          root.position.x = sampled.x;
          root.position.z = sampled.z;
          root.rotation.y = lerpAngle(root.rotation.y, sampled.facing, faceAlpha);
        }
      }

      // **The whirlwind: the body itself turns.** Phase 30.4.
      //
      // Here rather than in the pose, because this is `root.rotation.y` — the same angle facing
      // is written to — and two writers for one angle is how a body ends up pointing at the
      // average of two answers. A pose describes parts relative to the root; the spin *is* the
      // root.
      //
      // Driven straight off the server state, so a teammate's spin is visible to everyone: the
      // cast event that names the skill is sent only to the caster, and `CHANNELLING` is the one
      // thing every client is told. Advancing rather than assigning from a clock keeps each
      // character on its own angle instead of a party spinning in lockstep.
      if (snap.hp > 0 && snap.state === 'CHANNELLING') {
        entry.spinPhase += dt * SPIN_RADIANS_PER_SECOND;
        root.rotation.y = entry.spinPhase;
      }

      // **What is on this body, as a shade over it.** Phase 32.6, from
      // `doc/grind/design/STATUS-EFFECT-DISPLAY.md` §4. Applied from the snapshot every frame,
      // because an effect ends on a frame where nothing else about the body changes.
      applyStatusTint(entry.model.parts, snap, id === selfId ? selfPools : undefined);
      this.syncBuffMarker(entry, snap, id === selfId ? selfPools : undefined);

      // **What the status display decided, for the browser harness.** Phase 32.6, and the same
      // reasoning `__grindPos` below carries: Babylon is bundled rather than global and the store is
      // not on `window`, so a DOM-driven check can see that a HUD row exists but never whether a
      // *body* was shaded, and cannot see which effect won when two overlap. `renderOverlay` is not
      // reachable from the DOM at all.
      //
      // Read-only and behind `import.meta.env.DEV`, so it is absent from a production bundle — and
      // deliberately **not** a way to inject effects: a hook that could write them would verify the
      // layout while proving nothing about the server, which is the weaker check this exists to
      // avoid settling for.
      if (import.meta.env.DEV) {
        const probe = (
          window as unknown as { __grindStatus?: Record<string, unknown> }
        ).__grindStatus ?? {};
        probe[id] = {
          self: id === selfId,
          // Debuffs only: this probe walks every entity, and a buff is on the CHARACTER frame
          // rather than on a snapshot. The row's own buff half is covered by `statusEffects.test.ts`
          // and by the browser check, which drives the real toggle.
          effects: activeEffects(snap).map((e) => `${e.kind}:${e.seconds?.toFixed(1) ?? 'on'}`),
          tinted: entry.model.parts.some((part) => part.renderOverlay),
        };
        (window as unknown as { __grindStatus?: Record<string, unknown> }).__grindStatus = probe;
      }

      // Applied after the position, and outside both branches: a facing does not depend
      // on having a position to interpolate, and a remote player whose buffer has not
      // filled yet must still dive the right way rather than north.
      //
      // **And after the spin**, so a roll out of a whirlwind faces along the roll. The two
      // cannot overlap today — rolling cancels the channel on the server — but the roll is the
      // only explanation a player gets for why a hit missed, so it wins by construction rather
      // than by the server happening to agree.
      if (rollFacing !== null) root.rotation.y = rollFacing;

      // **Dev-only telemetry, for the browser harness.** What the local player's body is actually
      // doing, read after every writer has had its say.
      //
      // It exists because a whole class of fault is invisible without it. A dash, a roll or a
      // reconciliation *snap* is a large single-frame delta, and a whirlwind is a rotation — and
      // nothing else the harness can reach reports either. Babylon is bundled rather than global
      // and the store is not on `window`, so a DOM-driven check can see that a character moved
      // but never *how*, and cannot see it turn at all.
      //
      // **Below the rotation rather than beside the position**, deliberately: written where the
      // position is decided it would carry the previous frame's facing, which is exactly the
      // off-by-one that makes a trace of a turn unreadable.
      //
      // Behind `import.meta.env.DEV`, so it is absent from a production bundle entirely.
      if (import.meta.env.DEV && id === selfId) {
        (
          window as unknown as {
            __grindPos?: { x: number; z: number; facing: number; state?: string };
          }
        ).__grindPos = {
          x: root.position.x,
          z: root.position.z,
          facing: root.rotation.y,
          state: snap.state,
        };
      }

      // **An interrupted cast drops its animation.** A windowed cast that is still
      // waiting for its resolution while the server has stopped reporting CASTING was
      // abandoned — walked out of, rolled out of, or died in — and no resolution event
      // will ever arrive to end it. Left alone, the body would finish winding up and
      // strike at nothing, which is the one frame that would make the animation a lie
      // about the simulation.
      //
      // Safe against the resolving tick because the resolution event is drained at the
      // top of this method, before the per-entity pass: by the time the state is read
      // here, a completed cast has already cleared its flag.
      if (entry.castAwaiting && snap.state !== 'CASTING') {
        entry.castAwaiting = false;
        entry.castRemaining = 0;
      }

      // Tick the cast timer down before deciding the state, so the frame a cast
      // ends is the frame the body goes back to what it was doing.
      if (entry.castRemaining > 0) entry.castRemaining = Math.max(0, entry.castRemaining - dt);
      if (entry.dodgeRemaining > 0) entry.dodgeRemaining = Math.max(0, entry.dodgeRemaining - dt);

      // The roll's own timer is what decides the animation, not only the snapshot's
      // state. They agree for a remote player, but the local one starts rolling on the
      // frame of the keypress and the state arrives a tick later — reading the snapshot
      // alone would leave the body idle for the first 33 ms of a 300 ms roll, sliding.
      const state =
        entry.dodgeRemaining > 0 && snap.hp > 0
          ? 'dodge'
          : animStateFor(snap, entry.castRemaining > 0);

      // Death runs once and holds. Restarting it every frame the entity is dead
      // would make the corpse twitch.
      entry.deathProgress =
        state === 'death' ? Math.min(1, entry.deathProgress + dt / DEATH_SECONDS) : 0;

      // The stride is driven by ground actually covered, not by time, so the feet
      // do not skate when the server slows an entity down.
      const dx = root.position.x - entry.lastX;
      const dz = root.position.z - entry.lastZ;
      entry.lastX = root.position.x;
      entry.lastZ = root.position.z;

      this.ctx.state = state;
      this.ctx.cooldown = snap.cooldown;
      this.ctx.distance = Math.hypot(dx, dz);
      this.ctx.deathProgress = entry.deathProgress;
      // Elapsed rather than a fraction of the total, because the pose's progress is not
      // linear in time once a cast has a length: the wind-up stretches to the cast and
      // the follow-through stays fixed. See `castProgressFor`.
      this.ctx.castProgress = castProgressFor(
        entry.castTotal - entry.castRemaining,
        entry.castTimeSec,
      );
      this.ctx.castStyle = entry.castStyle;
      this.ctx.spinPhase = entry.spinPhase;
      this.ctx.dodgeProgress = 1 - entry.dodgeRemaining / DODGE_SECONDS;
      entry.state = state;

      entry.model.update(dt, this.ctx);
    }
  }

  /**
   * Starts a cast animation on an entity.
   *
   * Called from the store's event stream rather than from the snapshot's state, so the
   * wind-up begins on the tick the server opened the window instead of a tick later —
   * the same reason the roll's timer runs off `EVENT_DODGED`. Silently ignores an
   * entity that is not on screen: a skill cast by someone in another part of the zone
   * is a real event with nothing to animate.
   *
   * `castTimeSec` decides how long it runs. An instant skill gets the fixed 0.55 s
   * overlay it always had; a windowed one has its wind-up stretched to the window so
   * the body is still moving for as long as the server holds it.
   */
  startCast(id: string, style: CastStyle, castTimeSec = 0): void {
    const entry = this.tracked.get(id);
    if (!entry) return;
    entry.castTimeSec = castTimeSec;
    entry.castTotal = castAnimSeconds(castTimeSec);
    entry.castRemaining = entry.castTotal;
    // Only a windowed cast has a resolution to wait for, and only a windowed cast can
    // be interrupted — an instant one is over before anything could interrupt it.
    entry.castAwaiting = castTimeSec > 0;
    entry.castStyle = style;
  }

  /**
   * Lands a windowed cast's commit frame on the tick the server resolved it.
   *
   * The client's timer and the server's window drift by a frame or two, and the commit
   * is the frame the whole animation exists for — the same reason `attackPose` puts
   * contact on the damage tick. Snapping the elapsed time to the end of the wind-up
   * costs nothing and makes the strike coincide with the damage number.
   *
   * A no-op for an instant cast: its resolution arrives in the same tick as its start,
   * so snapping would skip a wind-up that is deliberate licence rather than a
   * discrepancy, turning all 85 instant skills into a twitch.
   */
  private commitCast(id: string, castTimeSec: number): void {
    if (castTimeSec <= 0) return;
    const entry = this.tracked.get(id);
    if (!entry || entry.castRemaining <= 0) return;

    entry.castAwaiting = false;
    entry.castRemaining = Math.min(entry.castRemaining, entry.castTotal - castTimeSec);
  }

  /**
   * Starts a roll animation on an entity, facing it along the roll.
   *
   * Called from the store's event stream. **Also clears any cast overlay**: a cast is a
   * timer over damage that has already been applied, so up to a quarter of a second of
   * it would otherwise resume when the roll finished, on a body that has moved four
   * units since.
   *
   * Silently ignores an entity that is not on screen, like `startCast` — someone rolling
   * elsewhere in the zone is a real event with nothing to animate.
   */
  startDodge(id: string, direction: { x: number; z: number }): void {
    const entry = this.tracked.get(id);
    if (!entry) return;
    entry.dodgeRemaining = DODGE_SECONDS;
    entry.castRemaining = 0;
    // A roll cancels the cast server-side, so the resolution will never come. Clearing
    // the flag stops the interrupt check from firing on a cast that is already gone.
    entry.castAwaiting = false;
    // Matches Go's Vec2.Angle — Atan2(X, Z), not the usual Atan2(y, x). A zero vector
    // leaves the facing alone rather than snapping the body to north.
    entry.dodgeFacing =
      direction.x !== 0 || direction.z !== 0 ? Math.atan2(direction.x, direction.z) : null;
  }

  /**
   * Returns the entity id a picked mesh belongs to, or null.
   *
   * Picks land on the invisible hitbox, which carries the id in `metadata`. The
   * parent walk covers a future model that nests geometry under a tagged node.
   */
  idForMesh(mesh: unknown): string | null {
    let node = mesh as { metadata?: unknown; parent?: unknown } | null | undefined;

    for (let depth = 0; node && depth < 8; depth++) {
      const id = (node.metadata as { entityId?: unknown } | undefined)?.entityId;
      if (typeof id === 'string') return id;
      node = node.parent as typeof node;
    }

    return null;
  }

  /**
   * Outlines an entity, or clears it.
   *
   * An outline rather than a tint: materials are shared across every entity of a
   * kind, so tinting one skeleton would light up all of them. `renderOutline` is
   * per-mesh state, which is exactly the granularity needed. It costs a second
   * draw of each part, which for the one thing under the cursor is nothing.
   */
  setHighlight(id: string, on: boolean): void {
    const entry = this.tracked.get(id);
    if (!entry) return;

    // The silhouette pass when it runs (`outline.ts`); per-mesh outlines when it does not.
    highlightParts(`entity:${id}`, entry.model.parts, on, HIGHLIGHT_WIDTH, HIGHLIGHT_COLOUR);
  }

  /**
   * Rebuilds every tracked character on the next update — for when the bodies and the generated
   * gear have loaded. A model is built once and rebuilt only on an equip, so a character drawn
   * before the assets landed (the player, always: the first snapshot beats a 3 MB fetch) would
   * otherwise stay in boxes, or bare-headed, until they next changed their gear. Through the
   * same rebuild an equip takes, so it is a discard and never a departure.
   */
  rebuildAll(): void {
    for (const entry of this.tracked.values()) entry.gearKey = '\u0000stale';
  }

  /** True when the id refers to something the player can attack. */
  isHostile(id: string): boolean {
    return this.tracked.get(id)?.kind === 'mob';
  }

  dispose(): void {
    // Cleared first, so a body that is still falling cannot be handed to the corpse list
    // by the removals below and outlive the registry that owns it.
    this.died.clear();
    for (const corpse of this.corpses) corpse.model.dispose();
    this.corpses.length = 0;

    for (const id of [...this.tracked.keys()]) this.remove(id);
    this.factory.dispose();
  }

  private create(id: string, snap: EntitySnapshot): Tracked {
    const model = this.factory.create(
      snap.kind,
      id === this.store.selfId(),
      id,
      snap.mesh,
      snap.armour,
      snap.weapon,
      wornGear(snap),
    );
    model.root.position.set(snap.x, 0, snap.z);
    model.root.rotation.y = snap.facing;

    // A pack champion is bigger. Scaling the root rather than the model means the
    // invisible collider grows with it — a larger enemy should be easier to click,
    // not merely harder to kill. Set once at creation: elite never changes for a
    // given entity, so this does not belong in the per-frame update.
    if (isElite(snap)) model.root.scaling.setAll(ELITE_SCALE);

    for (const part of model.parts) this.shadows.addShadowCaster(part);

    const entry: Tracked = {
      model,
      kind: snap.kind,
      armour: snap.armour,
      weapon: snap.weapon,
      gearKey: gearKey(snap),
      state: animStateFor(snap),
      // An entity already dead when first seen should be lying down, not
      // collapsing in front of us.
      deathProgress: snap.hp <= 0 ? 1 : 0,
      castRemaining: 0,
      castTotal: 0,
      castTimeSec: 0,
      castAwaiting: false,
      castStyle: 'weapon',
      dodgeRemaining: 0,
      dodgeFacing: null,
      spinPhase: 0,
      lastX: snap.x,
      lastZ: snap.z,
    };
    this.tracked.set(id, entry);
    return entry;
  }

  /**
   * An entity has **left**: it is gone from the snapshot.
   *
   * A death keeps the body for a moment; anything else — a player disconnecting, a zone
   * change — takes it away at once. A corpse for somebody who logged out would be a body
   * nobody killed.
   */
  /**
   * The orb a buff hangs over a body — the world half of *"every buff and debuff renders a
   * world effect"*.
   *
   * **A mesh, not a tint.** A body carries one overlay colour and the strongest effect wins, so
   * a shade could not show a stun and a shield at once; and the four debuff hues already crowd
   * the blue band (cold 195, defence 210, arcane 250), which is where a mana shield wants to be.
   * An orb over the head competes with none of that and is legible at a gameplay camera, which
   * is what the instruction actually asked for.
   *
   * **Built once per body and then only enabled or disabled.** Creating and disposing a mesh on
   * every toggle would allocate inside the render loop, which is the one thing this file may not
   * do; `setEnabled` is free. The material is shared per buff kind for the reason every material
   * here is shared — cloning per entity is what `setHighlight` exists to avoid.
   */
  private syncBuffMarker(entry: Tracked, snap: EntitySnapshot, pools?: Pools): void {
    const buff = activeEffects(snap, pools).find((e) => e.polarity === 'buff');

    if (!buff || snap.hp <= 0) {
      entry.buffOrb?.setEnabled(false);
      return;
    }

    if (!entry.buffOrb) {
      const orb = CreateSphere(`buff-orb-${buff.kind}`, { diameter: BUFF_ORB_RADIUS * 2, segments: 10 }, this.scene);
      orb.parent = entry.model.root;
      orb.position.set(0, BUFF_ORB_HEIGHT, 0);
      // Not a pick target and not a shadow caster: it is a readout, not part of the body.
      orb.isPickable = false;
      orb.material = this.buffMaterial(buff.kind);
      entry.buffOrb = orb;
    }
    entry.buffOrb.setEnabled(true);
  }

  /** One emissive material per buff kind, shared across every body wearing it. */
  private buffMaterial(kind: EffectKind): StandardMaterial {
    const cached = this.buffMaterials.get(kind);
    if (cached) return cached;

    const mat = new StandardMaterial(`buff-orb-mat-${kind}`, this.scene);
    const hue = Color3.FromHSV(EFFECT_HUE[kind], 0.55, 1);
    // Emissive rather than lit, so the orb reads the same wherever the body is standing — a
    // marker that goes dark in a shadowed corner is a marker the player stops trusting.
    mat.emissiveColor = hue;
    mat.diffuseColor = hue;
    mat.specularColor = Color3.Black();
    mat.alpha = 0.85;
    this.buffMaterials.set(kind, mat);
    return mat;
  }

  private readonly buffMaterials = new Map<EffectKind, StandardMaterial>();

  private remove(id: string): void {
    const model = this.detach(id);
    if (!model) return;

    if (this.died.has(id)) {
      this.startCorpse(model);
      return;
    }
    model.dispose();
  }

  /**
   * An entity's model is being **replaced**: same entity, new body.
   *
   * Never a corpse, whatever the last snapshot said died — see the note at the rebuild.
   */
  private discard(id: string): void {
    this.detach(id)?.dispose();
  }

  /** Unhooks a model from everything that points at it, returning it. */
  private detach(id: string): EntityModel | null {
    const entry = this.tracked.get(id);
    if (!entry) return null;
    this.setHighlight(id, false);
    for (const part of entry.model.parts) this.shadows.removeShadowCaster(part);
    // The orb is parented to the model root, but `EntityModel.dispose` is the model's own and
    // makes no promise about children this file hung on it. Disposed here, where everything else
    // that points at the body is unhooked.
    entry.buffOrb?.dispose();
    this.tracked.delete(id);
    return entry.model;
  }

  /**
   * Hands a dead entity's model to the corpse list.
   *
   * The mesh stops being a pick target here rather than at the end: a body you can click
   * is a body you can *target*, and targeting something the server has already forgotten
   * would leave the player attacking a ghost until they clicked elsewhere.
   */
  private startCorpse(model: EntityModel): void {
    for (const part of model.parts) part.isPickable = false;

    // Oldest first, so a pack wipe drops the body that is nearly faded rather than the one
    // that just fell. The same rule `BoltField` follows, for the same reason: the moment
    // every monster dies at once is the moment the frame budget is tightest.
    if (this.corpses.length >= MAX_CORPSES) {
      const oldest = this.corpses.shift();
      oldest?.model.dispose();
    }
    this.corpses.push({ model, elapsed: 0 });
  }

  /** Tips every corpse further over, fades it, and disposes the ones that are done. */
  private advanceCorpses(dt: number): void {
    for (let i = this.corpses.length - 1; i >= 0; i--) {
      const corpse = this.corpses[i];
      if (!corpse) continue;
      corpse.elapsed += dt;

      if (corpseFinished(corpse.elapsed)) {
        corpse.model.dispose();
        this.corpses.splice(i, 1);
        continue;
      }

      // **Rolled about x, not about the root's y.** `rotation.y` is the facing the body
      // died with and it is left alone: a corpse that snapped to a new heading as it fell
      // would read as the monster turning to look at you on the way down.
      corpse.model.root.rotation.x = fallAngle(corpse.elapsed);

      const alpha = corpseAlpha(corpse.elapsed);
      for (const part of corpse.model.parts) part.visibility = alpha;
    }
  }
}

/** Re-exported so main.tsx can position the camera without importing Babylon maths. */
export { Vector3 };

/**
 * How strong the status shade is. Phase 32.6.
 *
 * Low, and that is the whole tuning: this is a *body*, drawn at a gameplay camera among eleven
 * others, and a saturated overlay would hide the silhouette that says what a monster is. Enough to
 * read as "something is on this thing", not enough to replace its art.
 */
export const STATUS_TINT_ALPHA = 0.28;

/**
 * How wide a buff's rim is drawn.
 *
 * Wider than the targeting highlight, because it has to read at a gameplay camera without the
 * benefit of being the thing the player just clicked — and because a buff is a state the player
 * chose and wants confirmed, where a target rim is a transient answer to a pointer.
 */
/** The marker orb's radius and how high above the body's origin it floats, in world units. */
export const BUFF_ORB_RADIUS = 0.17;
export const BUFF_ORB_HEIGHT = 2.4;

/**
 * The shade a status puts on a body, as a per-mesh overlay.
 *
 * **Per-mesh, and that constraint decided the mechanism.** Materials are shared across every entity
 * of a kind — `setHighlight`'s comment says it in as many words: *"tinting one skeleton would light
 * up all of them"*. So this uses `renderOverlay`/`overlayColor`, which are per-mesh state exactly as
 * `renderOutline` is, and needs no material clone and no giving up the sharing.
 *
 * **It writes no shader**, which makes the spec's §4 warning milder than it feared: there is no GLSL
 * here to compile, so there is nothing SwiftShader could tolerate that a real driver would reject.
 * A `NullEngine` test can assert the property, and does. What still wants a real browser is whether
 * the shade is *legible* on a dark body at a gameplay camera, which is a judgement no assertion
 * makes.
 *
 * **The strongest effect wins, one shade at a time.** Two effects on one body would average to a
 * colour that names neither, so the hue is taken from the same longest-remaining ordering the HUD
 * row uses — what the icon row shows first is what the body wears.
 */
export function applyStatusTint(
  parts: readonly Mesh[],
  snap: EntitySnapshot,
  pools?: Pools,
): void {
  // Debuffs shade the body; a **buff hangs a marker over it** instead — see `syncBuffMarker`.
  // A body wears one shade (strongest wins), so putting both on this channel would make a
  // stunned-and-shielded character drop one of the two.
  const top = activeEffects(snap, pools).find((e) => e.polarity === 'debuff');

  if (!top || snap.hp <= 0) {
    // Cleared every frame it should not be showing, rather than only on the transition. An
    // effect ends on a frame where nothing else about the body changed, so there is no other
    // event to hang the clearing on — and a body left tinted is the exact frozen-field fault
    // the wire's delta comparison exists to prevent, reproduced on the render side.
    for (const part of parts) part.renderOverlay = false;
    return;
  }

  const colour = Color3.FromHSV(EFFECT_HUE[top.kind], 0.6, 0.9);
  for (const part of parts) {
    part.renderOverlay = true;
    part.overlayColor = colour;
    part.overlayAlpha = STATUS_TINT_ALPHA;
  }
}

/**
 * The four armour slots and the off hand, as the factory wants them.
 *
 * Pure and exported so the rebuild rule can be tested without a scene: a change to any
 * of these five is a change to the body and must rebuild it, exactly as the chest and
 * the main hand do.
 */
export function wornGear(snap: EntitySnapshot): WornGear {
  return {
    head: snap.head,
    hands: snap.hands,
    legs: snap.legs,
    feet: snap.feet,
    offhand: snap.offhand,
  };
}

/**
 * One string for the five slots, so "did any of them change" is one comparison. Absent
 * fields spell as empty, so a field that appears and one that disappears both change it.
 */
export function gearKey(snap: EntitySnapshot): string {
  return `${snap.head ?? ''}|${snap.hands ?? ''}|${snap.legs ?? ''}|${snap.feet ?? ''}|${snap.offhand ?? ''}`;
}
